import { ApiResponse, TicketManagerMember, TicketManagerTeam } from '@vision/ui/interfaces';
import { TicketManagerApi } from '../ticket-manager.api';
import { normalizeTicketManagerMembersResponse } from '../members';
import {
  TicketManagerTeamCreateArgs,
  TicketManagerTeamDeleteArgs,
  TicketManagerTeamMemberAddArgs,
  TicketManagerTeamMemberRemoveArgs,
  TicketManagerTeamMembersArgs,
  TicketManagerTeamUpdateArgs,
  TicketManagerProjectTeamsArgs,
} from './teams.api.types';
import { normalizeTicketManagerTeamResponse, normalizeTicketManagerTeamsResponse } from './teams.mappers';
import {
  buildAddTeamMemberRequest,
  buildCreateTeamRequest,
  buildDeleteTeamRequest,
  buildGetProjectTeamsRequest,
  buildGetTeamMembersRequest,
  buildRemoveTeamMemberRequest,
  buildUpdateTeamRequest,
} from './teams.routes';

export const TeamEndpoints = TicketManagerApi.injectEndpoints({
  endpoints: (builder) => ({
    deleteTicketManagerTeam: builder.mutation<void, TicketManagerTeamDeleteArgs>({
      query: (args) => ({
        ...buildDeleteTeamRequest(args),
        extraOptions: {
          notification: { autoHandleError: false, autoHandleSuccess: false },
        },
      }),
      invalidatesTags: (_result, error, { projectId }) =>
        error ? [] : [{ type: 'TicketManagerProjectTeams', id: projectId }],
    }),

    ticketManagerProjectTeams: builder.query<ApiResponse<TicketManagerTeam[]>, TicketManagerProjectTeamsArgs>({
      query: ({ projectId }) => buildGetProjectTeamsRequest({ projectId }),
      transformResponse: normalizeTicketManagerTeamsResponse,
      providesTags: (_result, _error, { projectId }) => [{ type: 'TicketManagerProjectTeams', id: projectId }],
    }),

    createTicketManagerTeam: builder.mutation<ApiResponse<TicketManagerTeam>, TicketManagerTeamCreateArgs>({
      query: ({ name, projectId }) => buildCreateTeamRequest({ name, projectId }),
      transformResponse: normalizeTicketManagerTeamResponse,
      invalidatesTags: (_result, _error, { projectId }) => [{ type: 'TicketManagerProjectTeams', id: projectId }],
    }),

    updateTicketManagerTeam: builder.mutation<ApiResponse<TicketManagerTeam>, TicketManagerTeamUpdateArgs>({
      query: ({ name, projectId, teamId }) => buildUpdateTeamRequest({ name, projectId, teamId }),
      transformResponse: normalizeTicketManagerTeamResponse,
      invalidatesTags: (_result, _error, { projectId }) => [{ type: 'TicketManagerProjectTeams', id: projectId }],
    }),

    ticketManagerTeamMembers: builder.query<ApiResponse<TicketManagerMember[]>, TicketManagerTeamMembersArgs>({
      query: ({ projectId, teamId }) => buildGetTeamMembersRequest({ projectId, teamId }),
      transformResponse: normalizeTicketManagerMembersResponse,
      providesTags: (_result, _error, { teamId }) => [{ type: 'TicketManagerTeamMembers', id: teamId }],
    }),

    addTicketManagerTeamMember: builder.mutation<Record<string, never>, TicketManagerTeamMemberAddArgs>({
      query: ({ projectId, role, teamId, userId }) => buildAddTeamMemberRequest({ projectId, role, teamId, userId }),
      invalidatesTags: (_result, _error, { projectId, teamId }) => [
        { type: 'TicketManagerTeamMembers', id: teamId },
        { type: 'TicketManagerProjectTeams', id: projectId },
      ],
    }),

    removeTicketManagerTeamMember: builder.mutation<Record<string, never>, TicketManagerTeamMemberRemoveArgs>({
      query: ({ projectId, teamId, userId }) => buildRemoveTeamMemberRequest({ projectId, teamId, userId }),
      invalidatesTags: (_result, _error, { projectId, teamId }) => [
        { type: 'TicketManagerTeamMembers', id: teamId },
        { type: 'TicketManagerProjectTeams', id: projectId },
      ],
    }),
  }),
});

export const {
  useDeleteTicketManagerTeamMutation,
  useTicketManagerProjectTeamsQuery,
  useCreateTicketManagerTeamMutation,
  useUpdateTicketManagerTeamMutation,
  useTicketManagerTeamMembersQuery,
  useAddTicketManagerTeamMemberMutation,
  useRemoveTicketManagerTeamMemberMutation,
} = TeamEndpoints;
